import { axiosInstance } from "@/axios/interceptors";
import { ZodSentryReporter } from "@/lib/zod-sentry-reporter";
import { QueryOptionsWithoutKeyAndFn } from "@/types/types";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { z } from "zod";

export const USER_AGREEMENT_DOCUMENT_KEYS = {
	guestSignatureStatus: (locationId: number) => [
		"guest-signature-status",
		locationId,
	],
	documentToBeSigned: (locationId: number) => [
		"document-to-be-signed",
		locationId,
	],
	getDocumentFilePath: (locationId: number) => ["get-document", locationId],
	getDocumentBlob: (path: string) => ["get-document-blob", path],
};

/** Response from GET guests/guest-signature-status */
export const GuestSignatureStatusSchema = z.object({
	signed: z.boolean(),
});
export type GuestSignatureStatus = z.infer<typeof GuestSignatureStatusSchema>;

const BlobSchema = z.instanceof(Blob);
type PDFUrlBlob = z.infer<typeof BlobSchema>;

export const DocumentToBeSignedSchema = z.object({
	id: z.number(),
	pdf_url: z.string(),
	blob: BlobSchema,
});
export type DocumentToBeSigned = z.infer<typeof DocumentToBeSignedSchema>;

export async function getGuestSignatureStatus(locationId: number) {
	const response = await axiosInstance.get(
		`locations/${locationId}/guests/guest-signature-status`,
	);
	return ZodSentryReporter.parse(GuestSignatureStatusSchema, response);
}

export async function fetchDocumentBlob(path: string): Promise<Blob> {
	const response = await axiosInstance.get("/global/download_document_file", {
		params: { path },
		responseType: "blob",
	});
	return ZodSentryReporter.parse(BlobSchema, response);
}

export function useDocumentPdfUrl(
	pdfPath: string,
	options: QueryOptionsWithoutKeyAndFn<PDFUrlBlob>,
) {
	return useQuery({
		queryKey: USER_AGREEMENT_DOCUMENT_KEYS.getDocumentBlob(pdfPath),
		queryFn: async () => {
			return fetchDocumentBlob(pdfPath);
		},
		staleTime: Number.POSITIVE_INFINITY,
		...options,
	});
}

export async function getDocumentToBeSigned(
	locationId: number,
): Promise<DocumentToBeSigned> {
	const response = await axiosInstance.get(
		`locations/${locationId}/guests/get-document-to-be-signed`,
	);
	const document = ZodSentryReporter.parse(
		DocumentToBeSignedSchema.omit({ blob: true }),
		response,
	);
	const blob = await fetchDocumentBlob(document.pdf_url);
	return { ...document, blob };
}

export interface SignDocumentPayload {
	locationId: number;
	documentId: number;
	signerFullName: string;
	signerDate: string;
	signature: Blob;
}

export async function signDocument(
	payload: SignDocumentPayload,
): Promise<unknown> {
	const { locationId } = payload;
	const signatureFile = new File([payload.signature], "signature.png", {
		type: "image/png",
	});
	const form = new FormData();
	form.append("document_id", String(payload.documentId));
	form.append("signer_full_name", payload.signerFullName);
	form.append("signer_date", payload.signerDate);
	form.append("signature", signatureFile);
	const response = await axiosInstance.post(
		`/locations/${locationId}/guests/sign-document`,
		form,
		{
			headers: {
				"Content-Type": "multipart/form-data",
			},
		},
	);
	return response.data;
}

export function useDocumentToBeSigned(locationId: number) {
	return useQuery({
		queryKey: USER_AGREEMENT_DOCUMENT_KEYS.getDocumentFilePath(locationId),
		queryFn: () => getDocumentToBeSigned(locationId),
		refetchOnWindowFocus: false,
	});
}

export const guestSignatureStatusQueryOptions = (locationId: number) =>
	queryOptions({
		queryKey: USER_AGREEMENT_DOCUMENT_KEYS.guestSignatureStatus(locationId),
		queryFn: () => getGuestSignatureStatus(locationId),
	});
